// pages/qna/QnAEditPage.tsx
import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {getQnADetail, updateQnA} from '../../api/deskApi';
import FileUploadComponent from '../../components/common/FileUploadComponent';

interface QnAForm {
    category: string;
    title: string;
    content: string;
}

interface ExistingFile {
    seq: number;
    fileName: string;
    fileUrl: string;
    fileSize?: number;
}

const QnAEditPage = () => {
    const navigate = useNavigate();
    const {id} = useParams<{ id: string }>();
    const [form, setForm] = useState<QnAForm>({
        category: '',
        title: '',
        content: '',
    });
    const [newFiles, setNewFiles] = useState<File[]>([]);
    const [existingFiles, setExistingFiles] = useState<ExistingFile[]>([]);
    const [deleteFileSeqs, setDeleteFileSeqs] = useState<number[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const categoryOptions = [
        {code: 'MEMBER', label: '회원'},
        {code: 'SERVICE', label: '서비스'},
        {code: 'PAYMENT', label: '선불'},
        {code: 'BILLING', label: '결제'},
        {code: 'ETC', label: '기타'},
    ];

    useEffect(() => {
        if (id) {
            fetchQnADetail(id);
        }
    }, [id]);

    const fetchQnADetail = async (qnaCode: string) => {
        try {
            setIsLoading(true);
            const response = await getQnADetail(qnaCode);

            if (response.data?.code === "00" && response.data.data) {
                const qna = response.data.data;
                setForm({
                    category: qna.category || '',
                    title: qna.questionTitle || '',
                    content: qna.questionContent || '',
                });

                // 기존 파일 목록 설정
                if (qna.questionFileList && qna.questionFileList.length > 0) {
                    setExistingFiles(qna.questionFileList);
                }
            } else {
                alert('문의 내역을 불러올 수 없습니다.');
                navigate('/desk/qna');
            }
        } catch (error) {
            console.error('Q&A 상세 조회 오류:', error);
            alert('문의 내역을 불러오는 중 오류가 발생했습니다.');
            navigate('/desk/qna');
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const {name, value} = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // 기존 파일 삭제
    const handleDeleteExistingFile = (fileSeq: number) => {
        setDeleteFileSeqs([...deleteFileSeqs, fileSeq]);
        setExistingFiles(existingFiles.filter(file => file.seq !== fileSeq));
    };

    const handleSubmit = async () => {
        if (!form.category) {
            alert('문의 유형을 선택해주세요.');
            return;
        }

        if (!form.title || !form.content) {
            alert('제목과 내용을 입력해주세요.');
            return;
        }

        if (isSubmitting || !id) return;

        try {
            setIsSubmitting(true);

            const formData = new FormData();
            formData.append('category', form.category);
            formData.append('questionTitle', form.title);
            formData.append('questionContent', form.content);

            // 삭제할 파일 seq 추가
            deleteFileSeqs.forEach(seq => {
                formData.append('deleteFileSeqs', seq.toString());
            });

            // 새로운 파일 추가
            newFiles.forEach((file) => {
                if (file) {
                    formData.append('files', file);
                }
            });

            const response = await updateQnA(id, formData);

            if (response.data?.code === "00") {
                alert('문의가 수정되었습니다.');
                navigate(`/desk/qna/${id}`);
            } else {
                alert(response.data?.message || '수정에 실패했습니다.');
            }
        } catch (error) {
            console.error('Q&A 수정 오류:', error);
            alert('문의 수정 중 오류가 발생했습니다.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatFileSize = (bytes?: number) => {
        if (!bytes) return '';
        const mb = bytes / (1024 * 1024);
        return `(${mb.toFixed(2)}MB)`;
    };

    if (isLoading) {
        return (
            <main className="container">
                <div className="inner-box">
                    <p style={{textAlign: 'center', padding: '50px 0'}}>로딩 중...</p>
                </div>
            </main>
        );
    }

    return (
        <>
            <div className="bottom-btn">
                <button type="button" className="btn cancel" onClick={() => navigate(-1)}>
                    취소
                </button>
                <button
                    type="button"
                    className="btn confirm"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? '수정 중...' : '수정완료'}
                </button>
            </div>

            <main className="container">
                <form className="form-box" onSubmit={(e) => e.preventDefault()}>
                    <section className="bg-white">
                        <fieldset>
                            <legend>1:1 문의 수정</legend>
                            <div className="inner-box">
                                <div className="form-item">
                                    <label htmlFor="category" className="input-tit">
                                        문의유형 선택
                                    </label>
                                    <div className="input-box">
                                        <select
                                            id="category"
                                            name="category"
                                            value={form.category}
                                            onChange={handleInputChange}
                                            disabled={isSubmitting}
                                        >
                                            <option value="" hidden>
                                                문의 유형을 선택하세요
                                            </option>
                                            {categoryOptions.map((option) => (
                                                <option key={option.code} value={option.code}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="form-item">
                                    <label htmlFor="title" className="input-tit">
                                        제목
                                    </label>
                                    <div className="input-box">
                                        <input
                                            id="title"
                                            type="text"
                                            name="title"
                                            value={form.title}
                                            onChange={handleInputChange}
                                            placeholder="문의 제목을 입력하세요"
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                </div>

                                <div className="form-item">
                                    <label htmlFor="content" className="input-tit">
                                        내용
                                    </label>
                                    <div className="input-box">
                                        <textarea
                                            id="content"
                                            name="content"
                                            value={form.content}
                                            onChange={handleInputChange}
                                            disabled={isSubmitting}
                                        ></textarea>
                                    </div>
                                </div>

                                {/* 기존 파일 목록 */}
                                {existingFiles.length > 0 && (
                                    <div className="form-item">
                                        <label className="input-tit">기존 첨부파일</label>
                                        <div className="existing-files">
                                            {existingFiles.map((file) => (
                                                <div key={file.seq} className="file-item" style={{
                                                    padding: '10px',
                                                    marginBottom: '5px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center'
                                                }}>
                                                    <span>
                                                        <i className="fa-solid fa-file"></i> {file.fileName} {formatFileSize(file.fileSize)}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteExistingFile(file.seq)}
                                                        disabled={isSubmitting}
                                                        style={{
                                                            padding: '5px 10px',
                                                            backgroundColor: '#dc3545',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        삭제
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* 새 파일 업로드 컴포넌트 */}
                                <FileUploadComponent
                                    files={newFiles}
                                    onFilesChange={setNewFiles}
                                    disabled={isSubmitting}
                                    maxFiles={3}
                                    maxSize={10}
                                />
                            </div>
                        </fieldset>
                    </section>
                </form>
            </main>
        </>
    );
};

export default QnAEditPage;